import type {
  Severity,
  CustomerRegisterItemStatus,
  ServiceClassificationType,
  RiskCategory,
  EffortEstimate,
  AuditEventType,
} from '@aigovops/core';

// Human-readable labels for enum values — used everywhere titles are rendered.

export const severityLabel: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  elevated: 'Elevated',
  moderate: 'Moderate',
  standard: 'Standard',
};

export const statusLabel: Record<CustomerRegisterItemStatus, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  review_needed: 'Review needed',
  done: 'Done',
  not_applicable: 'Not applicable',
  delegate_up: 'Escalate to lead',
};

export const classificationLabel: Record<ServiceClassificationType, string> = {
  agentic_systems: 'Agentic Systems',
  ai_agents: 'AI Agents',
  llm_services: 'LLM Services',
  ml_models: 'ML Models',
  data_pipelines: 'Data Pipelines',
};

export const categoryLabel: Record<RiskCategory, string> = {
  governance: 'Governance',
  access_control: 'Access Control',
  audit_logging: 'Audit Logging',
  incident_response: 'Incident Response',
  change_management: 'Change Management',
  data_governance: 'Data Governance',
  privacy: 'Privacy',
  security: 'Security',
  fairness_bias: 'Fairness & Bias',
  transparency: 'Transparency',
  safety: 'Safety',
  supply_chain: 'Supply Chain',
  content_safety: 'Content Safety',
  intellectual_property: 'Intellectual Property',
  environmental: 'Environmental',
};

export const categoryScope: Record<RiskCategory, string> = {
  governance:
    'Organizational oversight structures, board-level accountability, risk management frameworks, and AI strategy alignment.',
  access_control:
    'Identity management, authentication, authorization, privilege controls, and role-based access for AI systems and data.',
  audit_logging:
    'System activity recording, log retention, tamper detection, and audit trail completeness for AI operations.',
  incident_response:
    'Detection, escalation, containment, and recovery procedures for AI system failures, misuse, or security breaches.',
  change_management:
    'Version control, deployment approval, rollback procedures, and impact assessment for AI model and system changes.',
  data_governance:
    'Data quality, lineage, classification, retention, and lifecycle management for AI training and inference data.',
  privacy:
    'Personal data protection, consent management, data subject rights, and privacy impact assessments for AI processing.',
  security:
    'Technical safeguards, vulnerability management, penetration testing, and secure development practices for AI infrastructure.',
  fairness_bias:
    'Bias detection, demographic parity, outcome equity testing, and mitigation strategies for AI decision systems.',
  transparency:
    'Explainability, model documentation, decision rationale disclosure, and stakeholder communication about AI capabilities.',
  safety:
    'Operational safety boundaries, human override controls, failure mode analysis, and harm prevention for AI systems.',
  supply_chain:
    'Third-party AI component vetting, vendor risk assessment, model provenance, and dependency management.',
  content_safety:
    'Output filtering, harmful content detection, guardrails, and moderation policies for generative AI systems.',
  intellectual_property:
    'Training data licensing, model ownership, output IP rights, and protection against data extraction attacks.',
  environmental:
    'Energy consumption monitoring, carbon footprint reporting, and compute efficiency optimization for AI workloads.',
};

export const effortLabel: Record<EffortEstimate, string> = {
  XS: 'XS',
  S: 'S',
  M: 'M',
  L: 'L',
  XL: 'XL',
};

// BH16 (PR-P7-4): keyed by AuditEventType so a new event type that
// ships without a label fails `npm run build` here instead of
// silently falling back to the raw `namespace.action` string in the
// UI. The fallback in AuditLog.tsx (`eventTypeLabel[t] ?? t`) stays as
// defense-in-depth for values read straight from the DB, but this map
// itself must stay exhaustive.
export const eventTypeLabel: Record<AuditEventType, string> = {
  'item.status_changed': 'Status changed',
  'item.attested': 'Item attested',
  'item.reopened': 'Item reopened',
  'item.assignee_changed': 'Assignee changed',
  'item.reviewer_changed': 'Reviewer changed',
  'evidence.added': 'Evidence added',
  'policy.evaluated': 'Policy evaluated',
  'evidence.removed': 'Evidence removed',
  'evidence.superseded': 'Evidence superseded',
  'comment.added': 'Comment added',
  'methodology.bumped': 'Methodology updated',
  'readiness.score_changed': 'Score changed',
  'deadline.crossed': 'Deadline crossed',
  'package.signed': 'Package signed',
  'webhook.delivered': 'Webhook delivered',
  'webhook.test': 'Webhook test sent',
  'webhook.replayed': 'Webhook delivery replayed',
  'assessor.engagement_started': 'Engagement started',
  'assessor.engagement_ended': 'Engagement ended',
  'assessor.question_posted': 'Question posted',
  'assessor.question_addressed': 'Question addressed',
  'assessor.evidence_requested': 'Evidence requested',
  'assessor.evidence_provided': 'Evidence provided',
  'assessor.item_annotated': 'Item annotated',
  'assessor.annotation_disputed': 'Annotation disputed',
  'assessor.chain_verified': 'Chain verified',
  'audit.hold_placed': 'Hold placed',
  'audit.hold_released': 'Hold released',
  'audit.row_archived': 'Row archived',
  'question.assignee_changed': 'Question assignee changed',
  'alert.converted': 'Alert converted',
  'alert.closed': 'Alert closed',
  'alert.forwarded': 'Alert forwarded',
  'alert.forward_opened': 'Forward opened',
  'alert.forward_revoked': 'Forward revoked',
  'auth.sso_enabled': 'SSO enforcement enabled',
  'auth.sso_disabled': 'SSO enforcement disabled',
  'workspace.ai_policy_changed': 'AI provider policy changed',
  'workspace.data_mode_changed': 'Federal mode turned on',
  'workspace.field_class_raised': 'Data class raised',
  'workspace.ai_provider_registered': 'AI provider connected',
  'workspace.ai_provider_updated': 'AI provider updated',
  'workspace.ai_provider_disabled': 'AI provider disabled',
  'workspace.ai_provider_evaluated': 'AI provider evaluated',
  'workspace.ai_provider_approved': 'AI provider approved by owner',
  'organization.plan_changed': 'Organization plan changed',
  'llm.call': 'AI call',
  'use_case.submitted': 'Use case submitted',
  'use_case.review_started': 'Use case review started',
  'use_case.approved': 'Use case approved',
  'use_case.rejected': 'Use case rejected',
  'use_case.changes_requested': 'Use case changes requested',
  'use_case.activated': 'Use case activated',
  'use_case.returned_to_draft': 'Use case returned to draft',
  'use_case.re_review_triggered': 'Use case re-review triggered',
  'use_case.retired': 'Use case retired',
  'use_case.re_review_started': 'Use case re-review started',
  'finding.opened': 'Finding opened',
  'finding.acknowledged': 'Finding acknowledged',
  'finding.auto_closed': 'Finding auto-closed',
  'finding.closed': 'Finding closed',
  'finding.reopened': 'Finding reopened',
  'finding.sla_breached': 'Finding SLA breached',
  'finding.escalated': 'Finding escalated',
  'finding.remediation_drafted': 'Fix drafted',
  'finding.remediation_accepted': 'Fix used as plan',
  'finding.remediation_dismissed': 'Drafted fix dismissed',
  'finding.ticket_linked': 'Ticket linked',
  'finding.ticket_unlinked': 'Ticket unlinked',
  'check.passed': 'Check passed',
  'check.failed': 'Check failed',
  'check.error': 'Check error',
  'collector.created': 'In-network collector added',
  'collector.key_rotation_issued': 'Collector reinstall code issued',
  'collector.registered': 'Collector installed',
  'collector.revoked': 'Collector revoked',
  'verification.result_recorded': 'Verification result recorded',
  'verification.result_tampered': 'Verification result refused (tampered)',
  'verification.bundle_issued': 'Verification bundle issued',
  'verification.bundle_revoked': 'Verification bundle revoked',
  'verification.plan_locked': 'Infrastructure plan locked',
  'api_token.created': 'API token created',
  'ci_identity.created': 'CI workflow trusted',
  'ci_identity.disabled': 'CI workflow no longer trusted',
  'ci_identity.suggestion_dismissed': 'CI workflow marked not ours',
  'program.fields_changed': 'Program settings change',
  'program.fields_change_failed': 'Program settings change did not apply',
  'boundary.membership_added': 'System added to a program',
  'boundary.membership_removed': 'System taken out of a program',
  'ci_identity.pinned': 'CI workflow bound to its GitHub repository',
  'connector.configured': 'Connector configured',
  'connector.disabled': 'Connector disabled',
  'connector.error': 'Connector error',
  'source.failing': 'Source stopped collecting',
  'source.still_failing': 'Source still not collecting; evidence going stale',
  'connector.environment_changed': 'Connection environment changed',
  'connector.consent_started': 'Vendor console opened for a connection',
  'cloud_accounts.declared': 'Production cloud accounts declared',
  'clue.resolved': 'Discovery clue confirmed',
  'clue.dismissed': 'Discovery clue marked not ours',
  'clue.reopened': 'Discovery clue reopened',
  'clue.provider_pending': 'Discovery clue confirmed · provider pending',
  'connector.scan_mode_changed': 'Repository scan mode changed',
  'program.boundary_environments_changed': 'Environments counted for the program changed',
  'system_fact.confirmed': 'Discovered fact confirmed',
  'system_fact.dismissed': 'Discovered fact dismissed',
  'discovery.tool_found': 'AI tool discovered',
  'discovery.tool_sanctioned': 'AI tool sanctioned',
  'discovery.tool_blocked': 'AI tool block recommended',
  'provider.policy_changed': 'AI provider policy changed',
  'risk.accepted': 'Risk accepted',
  'risk.acceptance_closed': 'Risk acceptance closed',
  'risk.acceptance_expired': 'Risk acceptance expired',
  'incident.reported': 'Incident reported',
  'incident.status_changed': 'Incident status changed',
  'incident.obligation_notified': 'Incident obligation notified',
  'authority.drafted': 'Agent authority drafted',
  'authority.approved': 'Agent authority approved',
  'authority.rejected': 'Agent authority draft rejected',
  'scenario.run_completed': 'Risk scenario run completed',
  'supplier.grant_created': 'Supplier grant created',
  'supplier.grant_revoked': 'Supplier grant revoked',
  'disclosure.published': 'AI disclosure published',
  'disclosure.grant_read': 'AI disclosure read via grant',
  'system.classified': 'AI system classified',
  'system.classification_failed': 'AI system classification failed to persist',
  'organization.policy_source_changed': 'Organization policy source changed',
  'control_spec.applied_to_system': 'Control spec applied to system',
  'control_spec.proposed': 'Control spec proposed for review',
  'system.actor_roles_confirmed': 'Role for the system confirmed',
  'system.metadata_updated': 'System details updated from CI',
  'use_case.created': 'Use case filed from CI',
  'system.scan_results_reported': 'Local repository scan uploaded',
  'package.export_invalid': 'Export failed validation (not stored)',
  'package.export_too_large': 'Export too large (not stored)',
  'configuration_item.updated': 'Configuration item updated',
  'configuration_item.merged': 'Configuration items merged',
  'configuration_item.retired': 'Configuration item retired',
  'configuration_item.attributed': 'Configuration item linked to a system',
  'configuration_item.review_window_ended': 'Configuration item now counts (review window ended)',
  'configuration_item.review_completed': 'Configuration item reviewed',
  'monitoring_method.verified': 'Monitoring method verified',
  'system.class_declared': 'System class declared',
  'monitoring_candidate.accepted': 'Monitoring method accepted',
  'monitoring.config_changed': 'Monitored configuration changed',
  'anomaly_thresholds.updated': 'Anomaly thresholds changed',
  'monitoring_plan.signed': 'Monitoring plan signed',
  'insurability_pack.signed': 'Insurability pack signed',
  'insurability_pack.link_created': 'Pack link created',
  'insurability_pack.link_revoked': 'Pack link revoked',
  'insurability_pack.withdrawn': 'Insurability pack withdrawn',
  'monitoring.degradation_reopened': 'Control stopped passing again',
  'monitoring_feed.issued': 'Shared feed issued',
  'monitoring_feed.revoked': 'Shared feed revoked',
  'monitoring_feed.link_rotated': 'Shared feed link replaced',
  'monitoring_feed.token_rotated': 'Shared feed token replaced',
  'retention.run_completed': 'Old monitoring records removed',
  'webhook.endpoint_created': 'Webhook endpoint created',
  'webhook.endpoint_deleted': 'Webhook endpoint deleted',
  'webhook.secret_rotated': 'Webhook secret replaced',
  'webhook.test_sent': 'Webhook test event sent',
  'webhook.redelivered': 'Webhook delivery re-sent',
  'system_control.reopened': 'Control reopened (Not applicable cleared)',
  'jurisdiction_rule.applicability_chosen': 'Contested law: applicability decided',
};

// Bg + text color pairs for severity/status badges.
export const severityBadgeClass: Record<Severity, string> = {
  critical: 'bg-red-100 text-red-800 border border-red-200',
  high: 'bg-orange-100 text-orange-800 border border-orange-200',
  elevated: 'bg-amber-100 text-amber-800 border border-amber-200',
  moderate: 'bg-lime-100 text-lime-800 border border-lime-200',
  standard: 'bg-sky-100 text-sky-800 border border-sky-200',
};

export const statusBadgeClass: Record<CustomerRegisterItemStatus, string> = {
  todo: 'bg-red-50 text-red-700 border border-red-200',
  in_progress: 'bg-amber-50 text-amber-800 border border-amber-200',
  blocked: 'bg-orange-100 text-orange-800 border border-orange-200',
  review_needed: 'bg-blue-50 text-blue-700 border border-blue-200',
  done: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  not_applicable: 'bg-slate-100 text-slate-700 border border-slate-200',
  delegate_up: 'bg-gray-100 text-gray-600 border border-gray-200',
};

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * T6-S3: parse a display date. A bare `YYYY-MM-DD` (a report date, a period
 * end, a letter date) is a CALENDAR date: it is built in local time so it
 * shows as that same day everywhere. `new Date('2027-02-15')` would be UTC
 * midnight — Feb 14 in any timezone west of UTC — which is how a report date
 * of Feb 15 used to render as Feb 14. Full timestamps parse as instants.
 */
export function parseDisplayDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = DATE_ONLY.exec(iso);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** "Aug 22 – Nov 20, 2026" (one year when both ends share it), else both full dates. */
export function fmtRange(from: string | null | undefined, to: string | null | undefined): string {
  const a = parseDisplayDate(from);
  const b = parseDisplayDate(to);
  if (!a || !b) return `${fmtDate(from)} – ${fmtDate(to)}`;
  if (a.getFullYear() !== b.getFullYear()) return `${fmtDate(from)} – ${fmtDate(to)}`;
  const short = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${short(a)} – ${short(b)}, ${b.getFullYear()}`;
}

/** "1 item", "3 items" (T6 nit: pluralise counts). */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function daysUntil(iso: string | null | undefined, now = new Date()): number | null {
  if (!iso) return null;
  const t = parseDisplayDate(iso)?.getTime() ?? NaN;
  if (Number.isNaN(t)) return null;
  return Math.floor((t - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function scoreColor(score: number): string {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-lime-500';
  if (score >= 40) return 'bg-amber-500';
  if (score >= 20) return 'bg-orange-500';
  return 'bg-red-500';
}

/** Finding kinds as people say them (the findings.kind CHECK + legacy release/oversight/component kinds). */
const FINDING_KIND_LABELS: Record<string, string> = {
  control_check_failure: 'Failing check',
  supplier_material_delta: 'Supplier change',
  release_gap: 'Release gap',
  ksi_validation_failure: 'Failing KSI validation',
  vulnerability: 'Vulnerability',
  recurring_obligation_overdue: 'Overdue recurring task',
  significant_change: 'Significant change',
  sod_exception: 'Separation-of-duties exception',
  external_ai_outside_boundary: 'AI data leaving your network',
  ai_threat_finding: 'AI threat (cloud)',
  alternative_framework_lapse: 'Class A basis lapsed',
  oversight_gap: 'Oversight gap',
  component_delta: 'Component change',
  // v11.3 monitoring kinds (V113-5-4, D-113-14: every monitoring signal is a finding that Today and org alerts show).
  agent_authority_drift: 'Agent doing more than approved',
  classification_drift: 'System type disagrees with what we found',
  ai_usage_anomaly: 'Unusual AI usage',
  control_degradation: 'Control stopped passing',
  monitoring_testing_overdue: 'Monitoring test overdue',
  collector_unhealthy: 'Collector not reporting',
  ci_gone: 'Configuration item no longer seen',
};
export function findingKindLabel(kind: string): string {
  return FINDING_KIND_LABELS[kind] ?? kind.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Finding status as people say it (findings.status CHECK). */
const FINDING_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  remediated: 'Fixed · awaiting check',
  auto_closed: 'Closed · check passed',
  closed: 'Closed',
};
export function findingStatusLabel(status: string): string {
  return FINDING_STATUS_LABELS[status] ?? status.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}
